import { defineMessages, type MessageDescriptor } from 'react-intl'
import type { TripDetail } from 'tripPlanner/api/tripDetails'

const tripDetailsNudgeMessages = defineMessages({
  destinationQuestion: {
    id: '9eWaRJo1tzV34VzNcrWq',
    defaultMessage: 'Any destination in mind?',
    description: 'Question in the tooltip above the TripPlanner landing chat input when the traveller’s draft doesn’t say where they want to go',
  },
  datesQuestion: {
    id: 'xRg6bHkF35g-Psd3gQ_i',
    defaultMessage: 'Do you already know when?',
    description: 'Question in the tooltip above the TripPlanner landing chat input when the traveller’s draft doesn’t say when they want to travel',
  },
  travellersQuestion: {
    id: 'E-q673TcHOTTsI_WeDgX',
    defaultMessage: 'Who’s coming along?',
    description: 'Question in the tooltip above the TripPlanner landing chat input when the traveller’s draft doesn’t say who is travelling',
  },
  budgetQuestion: {
    id: 'DSp2rIkC0fw0dP7zjyrj',
    defaultMessage: 'Do you have a price range in mind?',
    description: 'Question in the tooltip above the TripPlanner landing chat input when the traveller’s draft doesn’t mention a budget or price range',
  },
  preferencesQuestion: {
    id: 'gnQMb_IquA9GK6FFuBhv',
    defaultMessage: 'What kind of trip are you after?',
    description: 'Question in the tooltip above the TripPlanner landing chat input when the traveller’s draft doesn’t say what kind of trip or stay they want',
  },
  tropicalLabel: {
    id: 'AXa8qVj0JaesrzHzhZGc',
    defaultMessage: 'Somewhere tropical',
    description: 'Answer button in the TripPlanner landing tooltip asking for a destination; tapping it adds a sentence about wanting somewhere tropical to the draft',
  },
  tropicalSentence: {
    id: 'R6TsDpGD396IEIs_aA1n',
    defaultMessage: 'We’d love somewhere tropical.',
    description: 'Sentence added to the traveller’s TripPlanner landing draft when they tap the "Somewhere tropical" answer to the destination question',
  },
  openToIdeasLabel: {
    id: 'oyLK_L4ZlcWqPukIg4rZ',
    defaultMessage: 'Open to ideas',
    description: 'Answer button in the TripPlanner landing tooltip asking for a destination; tapping it says the traveller has no destination in mind yet',
  },
  openToIdeasSentence: {
    id: 'JAGViT7lS-5NWY9bnstR',
    defaultMessage: 'We’re open to ideas on where to go.',
    description: 'Sentence added to the traveller’s TripPlanner landing draft when they tap the "Open to ideas" answer to the destination question',
  },
  nextMonthLabel: {
    id: 'u6DEsi_S8B72wtg28vbU',
    defaultMessage: 'Next month',
    description: 'Answer button in the TripPlanner landing tooltip asking when the traveller wants to go; tapping it says they hope to go next month',
  },
  nextMonthSentence: {
    id: 'cQZ2ZUiYhBNsBSCrG1ME',
    defaultMessage: 'We’re hoping to go next month.',
    description: 'Sentence added to the traveller’s TripPlanner landing draft when they tap the "Next month" answer to the travel dates question',
  },
  flexibleDatesLabel: {
    id: 'nmibo-f-VkPV4ZjWivOa',
    defaultMessage: 'Dates are flexible',
    description: 'Answer button in the TripPlanner landing tooltip asking when the traveller wants to go; tapping it says their travel dates are flexible',
  },
  flexibleDatesSentence: {
    id: 'BNZu3WXkF1hXQ-Zt-4A9',
    defaultMessage: 'Our dates are flexible.',
    description: 'Sentence added to the traveller’s TripPlanner landing draft when they tap the "Dates are flexible" answer to the travel dates question',
  },
  twoOfUsLabel: {
    id: 'iswjkRLdEsLKpwORPU8E',
    defaultMessage: 'Two of us',
    description: 'Answer button in the TripPlanner landing tooltip asking who is travelling; tapping it says the trip is for two people',
  },
  twoOfUsSentence: {
    id: 'qrW70i8tFTSyvHh3NJLr',
    defaultMessage: 'It’s just the two of us.',
    description: 'Sentence added to the traveller’s TripPlanner landing draft when they tap the "Two of us" answer to the question about who is travelling',
  },
  withKidsLabel: {
    id: 'PtZm1zX-mC3qYyZ8qj_W',
    defaultMessage: 'With the kids',
    description: 'Answer button in the TripPlanner landing tooltip asking who is travelling; tapping it says the traveller is bringing their children',
  },
  withKidsSentence: {
    id: 'Ge9cOw2QbJC5meOvxyub',
    defaultMessage: 'We’re bringing the kids.',
    description: 'Sentence added to the traveller’s TripPlanner landing draft when they tap the "With the kids" answer to the question about who is travelling',
  },
  goodValueLabel: {
    id: 'hgm1Oh4LrJN9Ou9SFoRn',
    defaultMessage: 'Good value',
    description: 'Answer button in the TripPlanner landing tooltip asking for a price range; tapping it says the traveller is looking for good value',
  },
  goodValueSentence: {
    id: 'XRKhBAcfupLZachicNDJ',
    defaultMessage: 'We’re after good value.',
    description: 'Sentence added to the traveller’s TripPlanner landing draft when they tap the "Good value" answer to the price range question',
  },
  splurgeLabel: {
    id: 'y4uXGf9PMggA801FCII9',
    defaultMessage: 'Happy to splurge',
    description: 'Answer button in the TripPlanner landing tooltip asking for a price range; tapping it says the traveller is happy to spend more',
  },
  splurgeSentence: {
    id: 'BcQin0SqWZ40sTdMNRtW',
    defaultMessage: 'We’re happy to splurge.',
    description: 'Sentence added to the traveller’s TripPlanner landing draft when they tap the "Happy to splurge" answer to the price range question',
  },
  relaxingLabel: {
    id: 'Bm8zJ7YzUmZUBktgnkVZ',
    defaultMessage: 'Relaxing',
    description: 'Answer button in the TripPlanner landing tooltip asking what kind of trip the traveller wants; tapping it says they want something relaxing',
  },
  relaxingSentence: {
    id: 'zAQTSN53mB4kBRBePL3P',
    defaultMessage: 'We’d love something relaxing.',
    description: 'Sentence added to the traveller’s TripPlanner landing draft when they tap the "Relaxing" answer to the question about the kind of trip',
  },
  adventurousLabel: {
    id: 'D2Tr3YsjRSvKYPOvkmB5',
    defaultMessage: 'Adventurous',
    description: 'Answer button in the TripPlanner landing tooltip asking what kind of trip the traveller wants; tapping it says they want some adventure',
  },
  adventurousSentence: {
    id: 'wRjA7QuzSkj_j9eAY_wu',
    defaultMessage: 'We’re after some adventure.',
    description: 'Sentence added to the traveller’s TripPlanner landing draft when they tap the "Adventurous" answer to the question about the kind of trip',
  },
  dismiss: {
    id: 'ZOOaSibvTud4dF13ifLE',
    defaultMessage: 'Stop asking about trip details',
    description: 'Accessibility label for the close button on the TripPlanner landing tooltip that asks about a missing trip detail; closing it stops these questions for the session',
  },
})

export const TRIP_DETAIL_QUESTION_MESSAGES: Record<TripDetail, MessageDescriptor> = {
  destination: tripDetailsNudgeMessages.destinationQuestion,
  dates: tripDetailsNudgeMessages.datesQuestion,
  travellers: tripDetailsNudgeMessages.travellersQuestion,
  budget: tripDetailsNudgeMessages.budgetQuestion,
  preferences: tripDetailsNudgeMessages.preferencesQuestion,
}

export default tripDetailsNudgeMessages
